import { useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { UseQueryResult } from "@tanstack/react-query";
import { useSearchParamsState } from "shared/hooks/use-search-params-state";
import { useApplicationSummary } from "shared/hooks/use-application-summary";
import { useApplicationDecision } from "shared/hooks/use-application-decision";
import { useApplicantDetail } from "shared/hooks/use-applicant-detail";
import { VerificationNames } from "shared/types";
import { Application } from "types/application";

export const useApplicantIdReportController = () => {
  const [searchParams, setSearchParams] = useSearchParamsState(
    {
      applicantId: "",
      report: VerificationNames.RentalApplication
    },
    ["applicantId", "report"]
  );

  const location = useLocation();

  //   get applicationId from url
  const { applicationId } = useParams();

  // Broad prefix so any mutation that invalidates this key cascades to
  // summary, decision, and per-applicant child caches via React Query
  // hierarchical key matching.
  const queryKey = ["APPLICATION", String(applicationId)];

  // The summary endpoint returns ApplicationSummary (a subset of Application).
  // Downstream components still type applicationMeta as UseQueryResult<Application>,
  // so we cast here. Fields absent from the summary are undefined at runtime;
  // the components only access fields that the summary actually provides.
  const applicationMeta = useApplicationSummary(
    applicationId
  ) as unknown as UseQueryResult<Application | undefined>;
  const applicationDecision = useApplicationDecision(applicationId);

  const applicantDetail = useApplicantDetail(
    applicationId,
    searchParams.applicantId || undefined
  );

  useEffect(() => {
    const data = applicationMeta.data;

    const urlParams = new URLSearchParams(location.search);
    const applicantId = urlParams.get("applicantId");

    const currentApplicantId = String(
      applicationMeta.data?.applicants.find(
        (appl) => String(appl.id) === String(applicantId)
      )?.id || data?.applicants[0]?.id
    );

    const reportName = new URLSearchParams(location.search).get("report");
    // Previously was in onSuccess
    setSearchParams({
      report: reportName || "rental_application",
      applicantId: currentApplicantId
    });
  }, [applicationMeta.data]);

  useEffect(() => {
    // check is searchParams is empty
    const urlParams = new URLSearchParams(location.search);
    setSearchParams({
      applicantId: urlParams.get("applicantId") || "",
      report: urlParams.get("report") || VerificationNames.RentalApplication
    });
  }, []);

  return {
    applicationMeta,
    applicationDecision,
    applicantDetail,
    searchParams,
    applicationId,
    setSearchParams,
    queryKey
  };
};
